import { inject, Injectable } from '@angular/core';
import {
  collection,
  CollectionReference,
  collectionData,
  doc,
  docData,
  DocumentData,
  DocumentReference,
  FirestoreInstances,
  orderBy,
  Query,
  query,
  limit,
  QueryConstraint,
  Timestamp,
  where,
  getCountFromServer,
} from '@angular/fire/firestore';
import { EnvironmentService } from '@galaxy/core';
import { SYSTEM_ASSISTANT_ID } from '@galaxy/ai-assistant';
import { from, map, Observable } from 'rxjs';
import { ConversationFilters } from './conversation-filters';
import { FIRESTORE_ZERO_TIMESTAMP } from './conversation.constants';
import {
  ConversationMessage,
  FirestoreConversation,
  FirestoreConversationLookup,
  FirestoreEvent,
} from './interface/conversation.interface';
import { PlatformLocation } from '@vendasta/conversation';
import { getParticipantTypeStr, Namespace } from './participant-utils';

const validOriginAppLocations = [
  'business_app',
  'partner_center',
  'sales_center',
  'vendor_center',
  'task_manager',
] as const;

type ValidOriginAppLocations = (typeof validOriginAppLocations)[number];

const getFirestore = (name: string) => {
  const fsi = inject(FirestoreInstances);
  return fsi.find((fs) => fs.app.name === name)!;
};

@Injectable()
export class FirestoreService {
  private firestore = getFirestore('inbox');
  private firestoreRootPath = `conversation/env/${this.environmentService.getEnvironmentString()}`;
  private firestoreLookupRootPath = `conversation-lookup/env/${this.environmentService.getEnvironmentString()}`;

  constructor(private readonly environmentService: EnvironmentService) {}

  private getConversationsCollection(): CollectionReference<FirestoreConversation> {
    return collection(this.firestore, this.firestoreRootPath) as CollectionReference<FirestoreConversation>;
  }

  private getLookupCollection(): CollectionReference<FirestoreConversationLookup> {
    return collection(this.firestore, this.firestoreLookupRootPath) as CollectionReference<FirestoreConversationLookup>;
  }

  private getFirestoreMessagesRootPath(conversationDocId: string): string {
    return `${this.firestoreRootPath}/${conversationDocId}/messages`;
  }

  private getFirestoreEventsRootPath(conversationDocId: string): string {
    return `${this.firestoreRootPath}/${conversationDocId}/events`;
  }

  conversation$(conversationDocId: string) {
    return docData<FirestoreConversation>(
      doc(this.firestore, `${this.firestoreRootPath}/${conversationDocId}`) as DocumentReference<FirestoreConversation>,
    );
  }

  setupBaseConversationsQuery(
    organizationId: string,
    filters?: ConversationFilters | null,
    groupId?: string | null,
    skipIsOpenInQuery = false,
    skipIsAnonymousInQuery = false,
  ): Query<FirestoreConversation> {
    let q = query<FirestoreConversation, DocumentData>(
      this.getConversationsCollection(),
      where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP.toDate()),
    );

    if (groupId) {
      q = query(q, where('subjectParticipantIds', 'array-contains', groupId));
    } else {
      q = query(q, where('subjectParticipantKeyIds', 'array-contains', organizationId));
    }

    // Anonymous should show all anonymous conversations regardless of if they've been closed
    if (!skipIsOpenInQuery && !filters?.isAnonymous) {
      q = query(q, where('isOpen', '==', filters?.isOpen ?? true));
    }

    if (!skipIsAnonymousInQuery) {
      q = query(q, where('isAnonymous', '==', filters?.isAnonymous ?? false));
    }

    return query(
      q,
      where('originApp', 'in', validOriginAppLocations),
      where('latestRelevantActivityTime', '!=', FIRESTORE_ZERO_TIMESTAMP.toDate()),
      where('originAppExternalID', '==', ''),
      orderBy('latestRelevantActivityTime', 'desc'),
    );
  }

  loadOrgRelatedConversationsQuery(organizationId: string): Query<FirestoreConversation> {
    const q = this.setupBaseConversationsQuery(organizationId);
    return query(q, where('channel', '==', 'platform'));
  }

  loadUserRelatedConversationsQuery(userId: string): Query<FirestoreConversation> {
    return query<FirestoreConversation, DocumentData>(
      this.getConversationsCollection(),
      where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP.toDate()),
      where('subjectParticipantKeyIds', 'array-contains', userId),
      where('originApp', 'in', validOriginAppLocations),
      where('latestRelevantActivityTime', '!=', FIRESTORE_ZERO_TIMESTAMP.toDate()),
      where('originAppExternalID', '==', ''),
      orderBy('latestRelevantActivityTime', 'desc'),
    );
  }

  // Queries for all conversations between the given user and a specific AIAssistant
  loadUserRelatedAiAssistantConversationsQuery(
    userId: string,
    assistantId: string,
    location: PlatformLocation,
    organization: Namespace,
  ): Query<FirestoreConversation> {
    let namespacedAssistantId = assistantId;
    if (assistantId !== SYSTEM_ASSISTANT_ID) {
      const orgTypeSnakeCase = getParticipantTypeStr(organization.type);
      namespacedAssistantId = `${assistantId}/${orgTypeSnakeCase}:${organization.id}`;
    }

    // Compute the pair key for efficient querying using the new index
    const pairKey = this.computePairKey(
      { type: 'ai_assistant', id: namespacedAssistantId },
      { type: 'iam_user', id: userId },
    );

    const constraints: QueryConstraint[] = [
      where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP.toDate()),
      where('subjectParticipantPairKeyIndex', 'array-contains', pairKey),
      where('originAppExternalID', '==', ''),
      where('channel', '==', 'AIAssistant'),
      where('latestRelevantActivityTime', '!=', FIRESTORE_ZERO_TIMESTAMP),
    ];

    let originApp: ValidOriginAppLocations | null = null;
    switch (location) {
      case PlatformLocation.PLATFORM_LOCATION_BUSINESS_APP:
        originApp = 'business_app';
        break;
      case PlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER:
        originApp = 'partner_center';
        break;
    }

    if (originApp) {
      constraints.push(where('originApp', '==', originApp));
    } else {
      constraints.push(where('originApp', 'in', validOriginAppLocations));
    }

    return query<FirestoreConversation, DocumentData>(
      this.getConversationsCollection(),
      ...constraints,
      orderBy('latestRelevantActivityTime', 'desc'),
    );
  }

  conversationCount(userId: string, location: PlatformLocation, channel: string): Observable<number> {
    let originApp: ValidOriginAppLocations | null = null;
    switch (location) {
      case PlatformLocation.PLATFORM_LOCATION_BUSINESS_APP:
        originApp = 'business_app';
        break;
      case PlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER:
        originApp = 'partner_center';
        break;
    }

    const q = query<FirestoreConversation, DocumentData>(
      this.getConversationsCollection(),
      where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP.toDate()),
      where('subjectParticipantKeyIds', 'array-contains', userId),
      where('channel', '==', channel),
      where('originApp', '==', originApp),
    );
    return from(getCountFromServer(q)).pipe(map((snapshot) => snapshot.data().count));
  }

  private computePairKey(
    participant1: { type: string; id: string },
    participant2: { type: string; id: string },
  ): string {
    const key1 = `${participant1.type}:${participant1.id}`;
    const key2 = `${participant2.type}:${participant2.id}`;
    return key1 < key2 ? `${key1}|${key2}` : `${key2}|${key1}`;
  }

  messages$(docId: string) {
    return collectionData(
      query(
        collection(this.firestore, this.getFirestoreMessagesRootPath(docId)),
        where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP.toDate()),
        orderBy('created', 'desc'),
      ),
      { idField: 'id' },
    ) as Observable<ConversationMessage[]>;
  }

  webchatMessages$(docId: string) {
    return collectionData(
      query(
        collection(this.firestore, this.getFirestoreMessagesRootPath(docId)),
        where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP.toDate()),
        where('channel', '==', 'WebChat'),
        where('type', 'in', ['message', 'booking_availability']),
        orderBy('created', 'desc'),
      ),
      { idField: 'id' },
    ) as Observable<ConversationMessage[]>;
  }

  events$(docId: string) {
    return collectionData(
      query(collection(this.firestore, this.getFirestoreEventsRootPath(docId)), orderBy('happenedAt', 'desc')),
    ) as Observable<FirestoreEvent[]>;
  }

  getViewsConversationsQuery(
    participantId: string,
    viewId: string,
    latestRelevantActivityTime?: Timestamp,
  ): Query<FirestoreConversation> {
    let q = query<FirestoreConversation, DocumentData>(
      this.getConversationsCollection(),
      where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP.toDate()),
      where('conversationViewIds', 'array-contains', participantId + ':' + viewId),
      where('originApp', 'in', validOriginAppLocations),
    );
    if (latestRelevantActivityTime) {
      q = query(q, where('latestRelevantActivityTime', '<=', latestRelevantActivityTime.toDate()));
    }
    return query(
      q,
      where('latestRelevantActivityTime', '!=', FIRESTORE_ZERO_TIMESTAMP.toDate()),
      where('originAppExternalID', '==', ''),
      orderBy('latestRelevantActivityTime', 'desc'),
    );
  }

  watchConversations$(baseQuery: Query<FirestoreConversation>, maxDocs: number): Observable<FirestoreConversation[]> {
    const q = query(baseQuery, limit(maxDocs));
    return collectionData(q, { idField: 'id' }) as Observable<FirestoreConversation[]>;
  }

  /**
   * Watch the ConversationLookup Firestore collection for a scope.
   * No filter fields — filtering (open/closed, anonymous, channel,
   * deleted) is the RPC's job. Purely a "something changed, refetch
   * now" trigger source.
   */
  watchLookupByScopeId$(
    scopeId: string,
    maxDocs: number,
  ): Observable<FirestoreConversationLookup[]> {
    const baseQuery = query<FirestoreConversationLookup, DocumentData>(
      this.getLookupCollection(),
      where('scopeId', '==', scopeId),
      orderBy('latestRelevantActivityTime', 'desc'),
    );
    const q = query(baseQuery, limit(maxDocs));
    return collectionData(q, { idField: 'id' }) as Observable<FirestoreConversationLookup[]>;
  }

  getUnreadConversationsByView(participantId: string, viewId: string): Query<FirestoreConversation> {
    return query<FirestoreConversation, DocumentData>(
      this.getConversationsCollection(),
      where('deleted', '==', FIRESTORE_ZERO_TIMESTAMP.toDate()),
      where('unreadConversationViewIds', 'array-contains', participantId + ':' + viewId),
      where('originApp', 'in', validOriginAppLocations),
      where('latestRelevantActivityTime', '!=', FIRESTORE_ZERO_TIMESTAMP.toDate()),
      where('originAppExternalID', '==', ''),
    );
  }
}
